import proj4 from 'proj4';

type LatLng = { lat: number; lng: number };

const MAX_POINTS = 30;

export type MapGeorefPin = {
  order?: number;
  x: number;
  y: number;
};

export type MapCorners = {
  topLeft: LatLng;
  topRight: LatLng;
  bottomLeft: LatLng;
};

export type MapGeorefResult = {
  corners: MapCorners;
  pinPositions: LatLng[];
};

type Point = { x: number; y: number };
type TmOrigin = { latitude: number; longitude: number };
type TrackSample = { xy: Point; gps: LatLng };
/** Affine weights: X = a x + b y + c, Y = d x + e y + f */
type Weights = [number, number, number, number, number, number];

const SEARCH_SAMPLES = 400;

const localTmProj = (origin: TmOrigin): string =>
  `+proj=tmerc +lat_0=${origin.latitude} +lon_0=${origin.longitude} +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs`;

const toProjected = (position: LatLng, origin: TmOrigin): Point => {
  const [x, y] = proj4('EPSG:4326', localTmProj(origin), [position.lng, position.lat]);
  return { x, y };
};

const fromProjected = (point: Point, origin: TmOrigin): LatLng => {
  const [lng, lat] = proj4(localTmProj(origin), 'EPSG:4326', [point.x, point.y]);
  return { lat, lng };
};

const dist = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

const applyWeights = (weights: Weights, x: number, y: number): Point => ({
  x: weights[0] * x + weights[1] * y + weights[2],
  y: weights[3] * x + weights[4] * y + weights[5]
});

const distPointToSegment = (p: Point, a: Point, b: Point): number => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 < 1e-12) return dist(p, a);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2));
  return dist(p, { x: a.x + t * abx, y: a.y + t * aby });
};

const downsampleTrack = (samples: TrackSample[]): TrackSample[] => {
  if (samples.length <= SEARCH_SAMPLES) return samples;
  const kept: TrackSample[] = [samples[0]];
  const minDist = Math.max(2, dist(samples[0].xy, samples[samples.length - 1].xy) / SEARCH_SAMPLES);
  for (let i = 1; i < samples.length; i++) {
    if (dist(samples[i].xy, kept[kept.length - 1].xy) >= minDist) kept.push(samples[i]);
  }
  const last = samples[samples.length - 1];
  if (kept[kept.length - 1] !== last) kept.push(last);
  if (kept.length <= SEARCH_SAMPLES) return kept;
  const even: TrackSample[] = [];
  for (let i = 0; i < SEARCH_SAMPLES; i++) {
    const idx = Math.round((i * (kept.length - 1)) / (SEARCH_SAMPLES - 1));
    if (even.length === 0 || even[even.length - 1] !== kept[idx]) even.push(kept[idx]);
  }
  return even;
};

const sortPinsByOrder = (pins: MapGeorefPin[]): MapGeorefPin[] =>
  pins
    .map((pin, index) => ({ pin, index }))
    .sort((a, b) => (a.pin.order ?? a.index) - (b.pin.order ?? b.index) || a.index - b.index)
    .map(({ pin }) => pin);

/** Course-ordered pair of pins with the largest pixel distance (A before B). */
const mostDistantPinPair = (pins: MapGeorefPin[]): [number, number] => {
  let bestI = 0;
  let bestJ = 1;
  let bestDist = -1;
  for (let i = 0; i < pins.length; i++) {
    for (let j = i + 1; j < pins.length; j++) {
      const d = dist({ x: pins[i].x, y: pins[i].y }, { x: pins[j].x, y: pins[j].y });
      if (d > bestDist) {
        bestDist = d;
        bestI = i;
        bestJ = j;
      }
    }
  }
  if (bestDist <= 0) {
    throw new Error('Cannot georeference map from pins: pins are too close together');
  }
  return [bestI, bestJ];
};

const weightsFromTwoTrackPoints = (
  pinA: MapGeorefPin,
  pinB: MapGeorefPin,
  sampleA: TrackSample,
  sampleB: TrackSample,
  reflect: boolean
): Weights | null => {
  const vx = pinB.x - pinA.x;
  const vy = pinB.y - pinA.y;
  const Vx = sampleB.xy.x - sampleA.xy.x;
  const Vy = sampleB.xy.y - sampleA.xy.y;
  const pixelVy = reflect ? -vy : vy;
  const lenPixel = Math.hypot(vx, vy);
  const lenGround = Math.hypot(Vx, Vy);
  if (lenPixel < 1e-6 || lenGround < 1e-6) return null;
  const scale = lenGround / lenPixel;
  const rot = Math.atan2(Vy, Vx) - Math.atan2(pixelVy, vx);
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const a = scale * cos;
  const b = reflect ? scale * sin : -scale * sin;
  const d = scale * sin;
  const e = reflect ? -scale * cos : scale * cos;
  return [
    a,
    b,
    sampleA.xy.x - a * pinA.x - b * pinA.y,
    d,
    e,
    sampleA.xy.y - d * pinA.x - e * pinA.y
  ];
};

const closestOnTrackRange = (
  p: Point,
  samples: TrackSample[],
  fromIndex: number,
  toIndex: number
): { dist: number; index: number } => {
  if (fromIndex > toIndex || fromIndex < 0 || toIndex >= samples.length) {
    return { dist: Number.POSITIVE_INFINITY, index: fromIndex };
  }
  let bestDist = dist(p, samples[fromIndex].xy);
  let bestIndex = fromIndex;
  for (let i = fromIndex + 1; i <= toIndex; i++) {
    const d = distPointToSegment(p, samples[i - 1].xy, samples[i].xy);
    if (d < bestDist) {
      bestDist = d;
      bestIndex = i;
    }
  }
  return { dist: bestDist, index: bestIndex };
};

/**
 * Pins are visited in course order. Anchor pins A and B are fixed to chosen track
 * samples; every other pin must land on the track between the previous bound and
 * the next remaining bound (pin A, pin B, then the end of the track).
 */
const orderedPredictionError = (
  weights: Weights,
  pins: MapGeorefPin[],
  samples: TrackSample[],
  pinAIndex: number,
  pinBIndex: number,
  trackA: number,
  trackB: number
): number => {
  let lastTrack = 0;
  let total = 0;
  let counted = 0;
  for (let p = 0; p < pins.length; p++) {
    if (p === pinAIndex) {
      lastTrack = trackA;
      continue;
    }
    if (p === pinBIndex) {
      lastTrack = trackB;
      continue;
    }
    const projected = applyWeights(weights, pins[p].x, pins[p].y);
    const hi = p < pinAIndex ? trackA : p < pinBIndex ? trackB : samples.length - 1;
    const closest = closestOnTrackRange(projected, samples, lastTrack, hi);
    total += closest.dist;
    lastTrack = closest.index;
    counted += 1;
  }
  if (counted === 0) return 0;
  return total / counted;
};

const isBetterCandidate = (current: { error: number } | null, error: number): boolean => {
  if (!current || !Number.isFinite(current.error)) return Number.isFinite(error);
  return error < current.error;
};

type MatchCandidate = { weights: Weights; error: number; indexA: number; indexB: number };

const considerCandidate = (
  best: MatchCandidate | null,
  pinA: MapGeorefPin,
  pinB: MapGeorefPin,
  sampleA: TrackSample,
  sampleB: TrackSample,
  pins: MapGeorefPin[],
  samples: TrackSample[],
  pinAIndex: number,
  pinBIndex: number,
  indexA: number,
  indexB: number
): MatchCandidate | null => {
  let current = best;
  // Image y is down, projected y is north: try reflection first so equal error keeps that orientation.
  for (const reflect of [true, false]) {
    const weights = weightsFromTwoTrackPoints(pinA, pinB, sampleA, sampleB, reflect);
    if (!weights) continue;
    const error = orderedPredictionError(weights, pins, samples, pinAIndex, pinBIndex, indexA, indexB);
    if (!Number.isFinite(error)) continue;
    if (!isBetterCandidate(current, error)) continue;
    current = { weights, error, indexA, indexB };
  }
  return current;
};

const searchPinPairOnTrackWindows = (
  pins: MapGeorefPin[],
  samples: TrackSample[],
  pinAIndex: number,
  pinBIndex: number,
  aFrom: number,
  aTo: number,
  bFrom: number,
  bTo: number,
  best: MatchCandidate | null
): MatchCandidate | null => {
  const n = samples.length;
  const aLo = Math.max(0, Math.min(aFrom, aTo));
  const aHi = Math.min(n - 1, Math.max(aFrom, aTo));
  const bLo = Math.max(0, Math.min(bFrom, bTo));
  const bHi = Math.min(n - 1, Math.max(bFrom, bTo));
  const pinA = pins[pinAIndex];
  const pinB = pins[pinBIndex];
  let current = best;
  for (let indexA = aLo; indexA <= aHi; indexA++) {
    for (let indexB = Math.max(indexA + 1, bLo); indexB <= bHi; indexB++) {
      current = considerCandidate(
        current,
        pinA,
        pinB,
        samples[indexA],
        samples[indexB],
        pins,
        samples,
        pinAIndex,
        pinBIndex,
        indexA,
        indexB
      );
    }
  }
  return current;
};

const matchOrderedPinsToTrack = (pins: MapGeorefPin[], samples: TrackSample[]): Weights => {
  const n = samples.length;
  const lastPinIndex = pins.length - 1;
  const firstWindowEnd = Math.min(MAX_POINTS, n) - 1;
  const lastWindowStart = Math.max(0, n - MAX_POINTS);

  const coarse = searchPinPairOnTrackWindows(
    pins,
    samples,
    0,
    lastPinIndex,
    0,
    firstWindowEnd,
    lastWindowStart,
    n - 1,
    null
  );
  if (!coarse) {
    throw new Error('Cannot georeference map from pins: no valid first/last pin match on the track');
  }

  const [pinAIndex, pinBIndex] = mostDistantPinPair(pins);
  const projectedA = applyWeights(coarse.weights, pins[pinAIndex].x, pins[pinAIndex].y);
  const projectedB = applyWeights(coarse.weights, pins[pinBIndex].x, pins[pinBIndex].y);
  const indexA = closestOnTrackRange(projectedA, samples, 0, n - 1).index;
  const indexB = closestOnTrackRange(projectedB, samples, Math.min(indexA + 1, n - 1), n - 1).index;

  const refined = searchPinPairOnTrackWindows(
    pins,
    samples,
    pinAIndex,
    pinBIndex,
    indexA - MAX_POINTS,
    indexA + MAX_POINTS,
    indexB - MAX_POINTS,
    indexB + MAX_POINTS,
    pinAIndex === 0 && pinBIndex === lastPinIndex ? coarse : null
  );

  return (refined ?? coarse).weights;
};

export const georeferenceMapFromPins = (
  width: number,
  height: number,
  pins: MapGeorefPin[],
  gpsTrack: LatLng[]
): MapGeorefResult => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error('Cannot georeference map from pins: invalid image size');
  }
  const validPins = sortPinsByOrder(pins.filter((pin) => Number.isFinite(pin.x) && Number.isFinite(pin.y)));
  const track = gpsTrack.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (validPins.length < 2) {
    throw new Error('Cannot georeference map from pins: at least two pins are required');
  }
  if (track.length < 2) {
    throw new Error('Cannot georeference map from pins: gpsTrack needs at least two points');
  }

  const origin: TmOrigin = {
    latitude: track.reduce((sum, p) => sum + p.lat, 0) / track.length,
    longitude: track.reduce((sum, p) => sum + p.lng, 0) / track.length
  };
  const samples = downsampleTrack(track.map((gps) => ({ xy: toProjected(gps, origin), gps })));
  const weights = matchOrderedPinsToTrack(validPins, samples);
  const apply = (x: number, y: number) => applyWeights(weights, x, y);
  return {
    corners: {
      topLeft: fromProjected(apply(0, 0), origin),
      topRight: fromProjected(apply(width, 0), origin),
      bottomLeft: fromProjected(apply(0, height), origin)
    },
    pinPositions: validPins.map((pin) => fromProjected(apply(pin.x, pin.y), origin))
  };
};
